/**
 * The ship's clock for the harbour's greeting: which watch is kept at a given hour, and how
 * many bells have been struck in it (one each half hour, eight at its end), as at sea.
 */

export const WATCHES = [
  'middle',
  'morning',
  'forenoon',
  'afternoon',
  'firstDog',
  'lastDog',
  'first',
] as const;
export type Watch = (typeof WATCHES)[number];
/** A bell of the watch, or an order of the day: one to eight. */
export type Eight = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

/** The watch kept at `date` (local time) and the bells struck so far, 1–8. */
export function shipsBell(date: Date): { watch: Watch; bells: Eight } {
  const minutes = date.getHours() * 60 + date.getMinutes();
  const hour = date.getHours();
  const watch: Watch =
    hour < 4
      ? 'middle'
      : hour < 8
        ? 'morning'
        : hour < 12
          ? 'forenoon'
          : hour < 16
            ? 'afternoon'
            : hour < 18
              ? 'firstDog'
              : hour < 20
                ? 'lastDog'
                : 'first';
  // Bells run through each four hours (the dog watches share theirs); the hour turns on eight.
  const half = Math.floor((minutes % 240) / 30);
  return { watch, bells: (half === 0 ? 8 : half) as Eight };
}

/** Ranks by engagements concluded, lowest first. */
export const RANKS = [
  { id: 'midshipman', from: 0 },
  { id: 'lieutenant', from: 1 },
  { id: 'commander', from: 5 },
  { id: 'captain', from: 10 },
  { id: 'commodore', from: 25 },
  { id: 'rearAdmiral', from: 50 },
] as const;
export type Rank = (typeof RANKS)[number];

/** The rank earned by `concluded` engagements, and the next one up (null at the top). */
export function rankOf(concluded: number): { rank: Rank; next: Rank | null } {
  let i = 0;
  while (i + 1 < RANKS.length && concluded >= (RANKS[i + 1]?.from ?? Infinity)) i++;
  return { rank: RANKS[i] ?? RANKS[0], next: RANKS[i + 1] ?? null };
}

/** How many orders of the day there are (`harbour.orders.o1`…); one a day, in turn. */
export const ORDERS = 18;
export type Order = Eight | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18;

export function orderOfTheDay(date: Date): Order {
  const day = Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86_400_000);
  return ((day % ORDERS) + 1) as Order;
}

/** The order after `n`, round to the first again. */
export const nextOrder = (n: Order): Order => ((n % ORDERS) + 1) as Order;
